/**
 * WriteOffDialog — Feature 4: Write-Offs
 *
 * Confirmation dialog for writing off one or more WIP items.
 * Supports optional write-off reason selection (controlled by the
 * "requireWriteOffReason" billing setting fetched from the API).
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, FileX, Loader2 } from "lucide-react";
import { fetchWriteOffReasons, fetchBillingSettings } from "@/lib/data-service";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface WriteOffItem {
  id: string;
  description: string;
  resource: string;
  type: "Time" | "Expense";
  amount: number;
  quantity?: number;
}

export interface WriteOffResult {
  itemIds: string[];
  reasonCode: string | null;
  reasonLabel: string | null;
}

interface WriteOffDialogProps {
  open: boolean;
  items: WriteOffItem[];
  formatCurrency: (n: number) => string;
  onClose: () => void;
  onConfirm: (result: WriteOffResult) => void;
}

// ── Fallback reasons (used if the API call fails) ─────────────────────────────
const FALLBACK_REASONS = [
  { code: "CLIENT_DISCOUNT", label: "Client Discount" },
  { code: "GOODWILL", label: "Goodwill Gesture" },
  { code: "ERROR_CORRECTION", label: "Error / Correction" },
  { code: "BUDGET_OVERRUN", label: "Budget Overrun" },
  { code: "INTERNAL", label: "Internal — Not Billable" },
  { code: "SCOPE_CHANGE", label: "Scope Change" },
  { code: "BAD_DEBT", label: "Bad Debt" },
  { code: "OTHER", label: "Other" },
];

// ── Component ─────────────────────────────────────────────────────────────────

export function WriteOffDialog({
  open,
  items,
  formatCurrency,
  onClose,
  onConfirm,
}: WriteOffDialogProps) {
  const [reasonCode, setReasonCode] = useState<string>("");
  const [requireReason, setRequireReason] = useState<boolean>(false);
  const [reasons, setReasons] = useState<{ code: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch reasons and settings from API when dialog opens
  useEffect(() => {
    if (!open) return;
    setReasonCode("");
    let cancelled = false;

    async function loadSettings() {
      setLoading(true);
      try {
        const [apiReasons, settings] = await Promise.all([
          fetchWriteOffReasons(),
          fetchBillingSettings(),
        ]);
        if (cancelled) return;
        const activeReasons = apiReasons
          .filter((r) => r.isActive)
          .map((r) => ({ code: r.code, label: r.label }));
        setReasons(activeReasons.length > 0 ? activeReasons : FALLBACK_REASONS);
        setRequireReason(settings.requireWriteOffReason);
      } catch {
        if (cancelled) return;
        setReasons(FALLBACK_REASONS);
        setRequireReason(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadSettings();
    return () => { cancelled = true; };
  }, [open]);

  const totalAmount = items.reduce((sum, i) => sum + i.amount, 0);
  const isBatch = items.length > 1;
  const canConfirm = !loading && (!requireReason || reasonCode !== "");

  const handleConfirm = () => {
    const resolved = reasons.find((r) => r.code === reasonCode) ?? null;
    onConfirm({
      itemIds: items.map((i) => i.id),
      reasonCode: reasonCode || null,
      reasonLabel: resolved?.label ?? null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileX className="h-5 w-5 text-muted-foreground" />
            Write Off {isBatch ? `${items.length} Items` : "WIP Item"}
          </DialogTitle>
          <DialogDescription>
            Written-off items will be excluded from WIP totals and cannot be invoiced.
            You can undo this action at any time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Items summary */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            {isBatch ? (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {items.length} WIP items selected
                </span>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1 flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{items[0]?.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {items[0]?.resource} · {items[0]?.type}
                      {items[0]?.type === "Time" && items[0]?.quantity != null
                        ? ` · ${items[0].quantity.toFixed(2)} hrs`
                        : ""}
                    </p>
                  </div>
                  <span className="text-sm font-medium shrink-0">
                    {formatCurrency(items[0]?.amount ?? 0)}
                  </span>
                </div>
              </>
            )}

            {isBatch && (
              <>
                <Separator />
                <div className="max-h-36 overflow-y-auto space-y-1.5">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between text-xs"
                    >
                      <span className="text-muted-foreground truncate mr-2">
                        {item.description}
                      </span>
                      <span className="shrink-0">
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                  ))}
                </div>
                <Separator />
                <div className="flex items-center justify-between text-sm font-semibold">
                  <span>Total write-off</span>
                  <span>{formatCurrency(totalAmount)}</span>
                </div>
              </>
            )}
          </div>

          {/* Reason selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Write-off reason
              {requireReason ? (
                <span className="text-destructive ml-1">*</span>
              ) : (
                <span className="text-muted-foreground ml-1">(optional)</span>
              )}
            </label>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading reasons...
              </div>
            ) : (
              <Select value={reasonCode} onValueChange={setReasonCode}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => (
                    <SelectItem key={r.code} value={r.code}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {requireReason && reasonCode === "" && !loading && (
              <p className="text-xs text-muted-foreground">
                Your organisation requires a reason for all write-offs.
              </p>
            )}
          </div>

          {/* Warning notice */}
          <div className="flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-warning-500" />
            <p>
              {isBatch
                ? `These ${items.length} items will be marked as written off and removed from your WIP totals.`
                : "This item will be marked as written off and removed from your WIP totals."}{" "}
              You can undo this from the Unbilled Work table at any time.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            Confirm Write-Off
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
